import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  Box, Typography, IconButton, Divider, Chip, Paper, MenuItem,
  TextField, CircularProgress, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import svLocale from '@fullcalendar/core/locales/sv';

// Activity type colors
const TYPE_COLORS = {
  phone:   '#2E7D32',
  email:   '#1565C0',
  meeting: '#6A1B9A',
  quote:   '#E65100',
  note:    '#FF6B00',
};

const TYPE_LABELS = {
  phone: 'Samtal', email: 'E-post', meeting: 'Möte',
  quote: 'Offert', note: 'Notering',
};

const ACTIVE_STAGES = ['identified', 'qualified', 'demo', 'sandbox', 'proposal', 'negotiation', 'pilot'];

export default function AffärerKalender() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all'); // all | followup | activity
  const [filterOwner, setFilterOwner] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [leadsSnap, usersSnap, deptsSnap] = await Promise.all([
        getDocs(query(collection(db, 'crmLeads'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'departments')),
      ]);

      const usersData = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const deptsData = deptsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(usersData);
      setDepartments(deptsData);

      const leads = leadsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const allEvents = [];

      // Load activities for each active lead
      for (const lead of leads) {
        const companyName = lead.companyName || '';
        const owner = usersData.find(u => u.id === lead.ownerId);
        const ownerName = owner?.displayName || owner?.email || '';

        // Next follow-up event
        if (lead.nextFollowUp) {
          allEvents.push({
            id: `followup-${lead.id}`,
            title: `📅 ${lead.title}${companyName ? ` · ${companyName}` : ''}`,
            date: lead.nextFollowUp,
            backgroundColor: ACTIVE_STAGES.includes(lead.status) ? '#FF6B00' : '#9E9E9E',
            borderColor: 'transparent',
            textColor: 'white',
            extendedProps: {
              type: 'followup',
              leadId: lead.id,
              leadTitle: lead.title,
              companyName,
              ownerName,
              ownerId: lead.ownerId,
              departmentIds: lead.departmentIds || [],
              status: lead.status,
            },
          });
        }

        // Activities
        try {
          const activitiesSnap = await getDocs(
            query(collection(db, 'crmLeads', lead.id, 'activities'), orderBy('date', 'desc'))
          );
          for (const actDoc of activitiesSnap.docs) {
            const act = actDoc.data();
            if (!act.date) continue;
            allEvents.push({
              id: `act-${actDoc.id}`,
              title: `${getTypeEmoji(act.type)} ${act.summary?.slice(0, 40) || TYPE_LABELS[act.type] || 'Aktivitet'}`,
              date: act.date,
              backgroundColor: TYPE_COLORS[act.type] || '#FF6B00',
              borderColor: 'transparent',
              textColor: 'white',
              extendedProps: {
                type: 'activity',
                activityType: act.type,
                leadId: lead.id,
                leadTitle: lead.title,
                companyName,
                summary: act.summary,
                ownerName,
                ownerId: lead.ownerId,
                departmentIds: lead.departmentIds || [],
              },
            });
          }
        } catch (e) { /* skip */ }
      }

      setEvents(allEvents);
      setLoading(false);
    }
    load();
  }, []);

  function getTypeEmoji(type) {
    return { phone: '📞', email: '📧', meeting: '📅', quote: '📄', note: '📝' }[type] || '🔹';
  }

  const filtered = events.filter(ev => {
    if (filterType === 'followup' && ev.extendedProps.type !== 'followup') return false;
    if (filterType === 'activity' && ev.extendedProps.type !== 'activity') return false;
    if (filterOwner && ev.extendedProps.ownerId !== filterOwner) return false;
    if (filterDept && !(ev.extendedProps.departmentIds || []).includes(filterDept)) return false;
    return true;
  });

  const today = new Date().toISOString().split('T')[0];
  const upcoming = filtered.filter(e =>
    e.extendedProps.type === 'followup' && e.date >= today
  ).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Affärer – Kalender</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              Uppföljningar och aktiviteter
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        {/* Filter bar */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Type filter chips */}
          {[
            { key: 'all',      label: 'Alla' },
            { key: 'followup', label: '📅 Uppföljningar' },
            { key: 'activity', label: '🔹 Aktiviteter' },
          ].map(f => (
            <Chip key={f.key} label={f.label} size="small"
              onClick={() => setFilterType(f.key)}
              variant={filterType === f.key ? 'filled' : 'outlined'}
              sx={{ fontWeight: filterType === f.key ? 700 : 400,
                bgcolor: filterType === f.key ? '#1a1a1a' : 'transparent',
                color: filterType === f.key ? 'white' : 'text.primary' }} />
          ))}
          <Box sx={{ flex: 1 }} />
          <TextField select size="small" value={filterOwner}
            onChange={e => setFilterOwner(e.target.value)}
            sx={{ minWidth: 180, bgcolor: 'white', borderRadius: 1 }} label="Säljare">
            <MenuItem value="">Alla säljare</MenuItem>
            {users.map(u => <MenuItem key={u.id} value={u.id}>{u.displayName || u.email}</MenuItem>)}
          </TextField>
          <TextField select size="small" value={filterDept}
            onChange={e => setFilterDept(e.target.value)}
            sx={{ minWidth: 200, bgcolor: 'white', borderRadius: 1 }} label="Affärsområde">
            <MenuItem value="">Alla affärsområden</MenuItem>
            {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </TextField>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Calendar */}
            <Box sx={{ flex: 1, minWidth: 600 }}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <style>{`
                  .fc .fc-button { background: #1a1a1a !important; border-color: #1a1a1a !important; }
                  .fc .fc-button:hover { background: #333 !important; }
                  .fc .fc-button-active { background: #FF6B00 !important; border-color: #FF6B00 !important; }
                  .fc-event { cursor: pointer; font-size: 0.72rem !important; }
                  .fc .fc-daygrid-event { border-radius: 4px !important; }
                  .fc-today-button { text-transform: capitalize !important; }
                  .fc .fc-col-header-cell-cushion { font-weight: 600; color: #1a1a1a; }
                  .fc .fc-daygrid-day-number { color: #333; }
                  .fc .fc-day-today { background: #fff8f0 !important; }
                `}</style>
                <FullCalendar
                  plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  locale={svLocale}
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,listWeek,listMonth',
                  }}
                  buttonText={{ listWeek: 'Lista vecka', listMonth: 'Lista månad' }}
                  events={filtered}
                  height="auto"
                  eventClick={info => {
                    const { leadId } = info.event.extendedProps;
                    if (leadId) navigate(`/crm/leads/${leadId}`);
                  }}
                  eventContent={arg => (
                    <Tooltip title={
                      <Box>
                        <Typography variant="caption" fontWeight="bold">
                          {arg.event.extendedProps.leadTitle}
                        </Typography>
                        {arg.event.extendedProps.companyName && (
                          <Typography variant="caption" display="block">
                            {arg.event.extendedProps.companyName}
                          </Typography>
                        )}
                        {arg.event.extendedProps.summary && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            {arg.event.extendedProps.summary}
                          </Typography>
                        )}
                        {arg.event.extendedProps.ownerName && (
                          <Typography variant="caption" display="block" sx={{ color: '#ccc' }}>
                            {arg.event.extendedProps.ownerName}
                          </Typography>
                        )}
                      </Box>
                    }>
                      <Box sx={{
                        px: 0.5, py: 0.2, overflow: 'hidden',
                        whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                        fontSize: '0.7rem', cursor: 'pointer',
                      }}>
                        {arg.event.title}
                      </Box>
                    </Tooltip>
                  )}
                />
              </Paper>
            </Box>

            {/* Upcoming follow-ups sidebar */}
            <Box sx={{ width: 280, flexShrink: 0 }}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5,
                    color: 'text.secondary', mb: 1.5 }}>
                  Kommande uppföljningar
                </Typography>
                {upcoming.length === 0 ? (
                  <Typography variant="body2" color="text.disabled">
                    Inga planerade uppföljningar
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {upcoming.slice(0, 15).map(ev => {
                      const isToday = ev.date === today;
                      const isPast = ev.date < today;
                      return (
                        <Box key={ev.id}
                          onClick={() => navigate(`/crm/leads/${ev.extendedProps.leadId}`)}
                          sx={{
                            p: 1.5, borderRadius: 1.5, cursor: 'pointer',
                            bgcolor: isToday ? '#FFF3E0' : isPast ? '#FFEBEE' : '#f9f9f9',
                            borderLeft: `3px solid ${isPast ? '#C62828' : isToday ? '#FF6B00' : '#ccc'}`,
                            '&:hover': { bgcolor: '#f0f0f0' },
                          }}>
                          <Typography variant="caption" fontWeight="bold"
                            sx={{ color: isPast ? '#C62828' : isToday ? '#FF6B00' : '#555',
                              display: 'block' }}>
                            {ev.date}
                            {isToday && ' – IDAG'}
                            {isPast && ' – FÖRFALLEN'}
                          </Typography>
                          <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.3, mt: 0.3 }}>
                            {ev.extendedProps.leadTitle}
                          </Typography>
                          {ev.extendedProps.companyName && (
                            <Typography variant="caption" color="text.disabled">
                              {ev.extendedProps.companyName}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Paper>

              {/* Legend */}
              <Paper sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                  Förklaring
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#FF6B00' }} />
                    <Typography variant="caption">Uppföljning (aktiv)</Typography>
                  </Box>
                  {Object.entries(TYPE_COLORS).map(([key, color]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: color }} />
                      <Typography variant="caption">{TYPE_LABELS[key]}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
